/**
 * What the transcript should do with the scroll position on this render.
 *
 * - `none` — nothing to position yet (the conversation is still loading).
 * - `jump-to-end` — land at the bottom instantly, with no animation.
 * - `anchor-top` — put the top of a long new reply at the top of the viewport.
 * - `smooth-to-end` — the ordinary chat scroll down to the newest message.
 */
export type ChatScrollAction = 'none' | 'jump-to-end' | 'anchor-top' | 'smooth-to-end';

/**
 * Opening a conversation is not the same event as receiving a message, and they want opposite
 * scrolling.
 *
 * A conversation that is being opened has never been on screen, so there is nothing to animate
 * FROM — the smooth scroll only showed the user the top of a thread they did not ask to see,
 * followed by a long ride to the bottom. It jumps.
 *
 * The catch this encodes: the list mounts while the fetch is still in flight, so the first render
 * of a conversation has NO messages. "First render" is therefore the wrong trigger — it would fire
 * on an empty transcript and still be unspent when the messages arrived, which is exactly the
 * render that was animating. The trigger is the first render that actually has something to
 * scroll to.
 */
export function chatScrollAction(input: {
  /** Whether this conversation has already been put in its opening position. */
  hasPositioned: boolean;
  messageCount: number;
  /** A genuinely new assistant reply arrived (not a re-render, not a failed send). */
  isNewReply: boolean;
  /** That reply is taller than the viewport, so its start would otherwise be scrolled past. */
  replyTallerThanViewport: boolean;
}): ChatScrollAction {
  if (!input.hasPositioned) {
    return input.messageCount === 0 ? 'none' : 'jump-to-end';
  }
  if (input.isNewReply && input.replyTallerThanViewport) return 'anchor-top';
  return 'smooth-to-end';
}
